import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Header } from '../../components/Header/Header';
import { Avatar } from '../../components/Avatar';
import { Toast } from '../../components/Toast/Toast';
import { ClientEditor } from '../../components/ClientEditor/ClientEditor';
import { PencilIcon, PlusIcon, TrashIcon } from '../../components/Icon';
import { useStudioData } from '../../hooks/useStudioData';
import type { NewClientInput } from '../../hooks/useStudioData';
import { useToast } from '../../hooks/useToast';
import { totalOpen } from '../../lib/selectors';
import styles from './ClientsTable.module.css';

/** A single inline-editable table cell; commits on blur / Enter. */
function EditableCell({
  value,
  onCommit,
  placeholder,
  className,
  type = 'text',
}: {
  value: string | undefined;
  onCommit: (next: string) => void;
  placeholder?: string;
  className?: string;
  type?: string;
}) {
  const [draft, setDraft] = useState(value ?? '');

  const commit = () => {
    const next = draft.trim();
    if (next !== (value ?? '')) onCommit(next);
  };

  return (
    <input
      type={type}
      className={className ? `${styles.cellInput} ${className}` : styles.cellInput}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        else if (e.key === 'Escape') {
          setDraft(value ?? '');
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** The trailing row for creating a new client. */
function NewClientRow({ onAdd }: { onAdd: (data: NewClientInput) => void }) {
  const [name, setName] = useState('');
  const [initials, setInitials] = useState('');
  const [website, setWebsite] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const submit = () => {
    if (!name.trim()) return;
    onAdd({ name, initials, website, contactEmail, phone, notes });
    setName('');
    setInitials('');
    setWebsite('');
    setContactEmail('');
    setPhone('');
    setNotes('');
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') submit();
  };

  return (
    <tr className={styles.newRow}>
      <td className={styles.avatarCell} />
      <td>
        <input
          className={styles.cellInput}
          value={name}
          placeholder="New client name…"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onKey}
        />
      </td>
      <td>
        <input
          className={`${styles.cellInput} ${styles.initialsInput}`}
          value={initials}
          placeholder="AUTO"
          onChange={(e) => setInitials(e.target.value)}
          onKeyDown={onKey}
        />
      </td>
      <td>
        <input
          className={styles.cellInput}
          value={website}
          placeholder="example.com"
          onChange={(e) => setWebsite(e.target.value)}
          onKeyDown={onKey}
        />
      </td>
      <td>
        <input
          className={styles.cellInput}
          value={contactEmail}
          placeholder="name@example.com"
          onChange={(e) => setContactEmail(e.target.value)}
          onKeyDown={onKey}
        />
      </td>
      <td>
        <input
          className={styles.cellInput}
          value={phone}
          placeholder="+1…"
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={onKey}
        />
      </td>
      <td>
        <input
          className={styles.cellInput}
          value={notes}
          placeholder="Notes…"
          onChange={(e) => setNotes(e.target.value)}
          onKeyDown={onKey}
        />
      </td>
      <td className={styles.count}>—</td>
      <td className={styles.count}>—</td>
      <td className={styles.count}>—</td>
      <td className={styles.actions}>
        <button
          type="button"
          className={styles.addBtn}
          onClick={submit}
          aria-label="Add client"
          title="Add client"
        >
          <PlusIcon size={14} />
        </button>
      </td>
    </tr>
  );
}

export function ClientsTable() {
  const data = useStudioData();
  const { message, flash } = useToast();
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const editingClient = data.clients.find((c) => c.id === editingClientId) ?? null;

  const onCapture = (title: string) => {
    if (!title.trim()) return;
    data.captureTriage(title);
    flash('Captured to triage');
  };

  const onDelete = (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    data.deleteClient(id);
    flash('Client deleted');
  };

  const onAdd = (input: NewClientInput) => {
    data.addClient(input);
    flash('Client added');
  };

  return (
    <div className={styles.page}>
      <div className="grain" />
      <Header
        totalOpen={totalOpen(data.clients)}
        clientCount={data.clients.length}
        onCapture={onCapture}
      />

      <main className={styles.main}>
        <div className={styles.head}>
          <h2 className={styles.title}>Clients</h2>
          <span className={styles.hint}>
            Click any cell to edit · changes save automatically
          </span>
        </div>

        <div className={`${styles.tableWrap} sb-scroll`}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th aria-label="Avatar" />
                <th>Name</th>
                <th>Initials</th>
                <th>Website</th>
                <th>Contact email</th>
                <th>Phone</th>
                <th>Notes</th>
                <th>Projects</th>
                <th>Domains</th>
                <th>Contacts</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.clients.map((client) => (
                <tr key={client.id}>
                  <td className={styles.avatarCell}>
                    <Avatar client={client} size={32} fontSize="0.72rem" />
                  </td>
                  <td>
                    <EditableCell
                      value={client.name}
                      placeholder="Name"
                      onCommit={(v) => data.updateClient(client.id, { name: v })}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={client.initials}
                      placeholder="—"
                      className={styles.initialsInput}
                      onCommit={(v) => data.updateClient(client.id, { initials: v })}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={client.website}
                      placeholder="—"
                      onCommit={(v) => data.updateClient(client.id, { website: v })}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={client.contactEmail}
                      placeholder="—"
                      type="email"
                      onCommit={(v) => data.updateClient(client.id, { contactEmail: v })}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={client.phone}
                      placeholder="—"
                      type="tel"
                      onCommit={(v) => data.updateClient(client.id, { phone: v })}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={client.notes}
                      placeholder="—"
                      onCommit={(v) => data.updateClient(client.id, { notes: v })}
                    />
                  </td>
                  <td className={styles.count}>{client.projects?.length ?? 0}</td>
                  <td className={styles.count}>
                    <button
                      type="button"
                      className={styles.countBtn}
                      onClick={() => setEditingClientId(client.id)}
                      title="Edit additional domains"
                    >
                      {client.additionalDomains?.length ?? 0}
                    </button>
                  </td>
                  <td className={styles.count}>
                    <button
                      type="button"
                      className={styles.countBtn}
                      onClick={() => setEditingClientId(client.id)}
                      title="Edit additional contacts"
                    >
                      {client.additionalContacts?.length ?? 0}
                    </button>
                  </td>
                  <td className={styles.actions}>
                    <button
                      type="button"
                      className={`${styles.iconBtn} ${styles.editBtn}`}
                      onClick={() => setEditingClientId(client.id)}
                      aria-label={`Edit ${client.name}`}
                      title="Edit client"
                    >
                      <PencilIcon size={14} />
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => onDelete(client.id, client.name)}
                      aria-label={`Delete ${client.name}`}
                      title="Delete client"
                    >
                      <TrashIcon size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              <NewClientRow onAdd={onAdd} />
            </tbody>
          </table>
        </div>

        {data.clients.length === 0 && !data.loading && (
          <div className={styles.empty}>No clients yet — add one below.</div>
        )}
      </main>

      {editingClient && (
        <ClientEditor
          client={editingClient}
          onClose={() => setEditingClientId(null)}
          onSave={(patch) => {
            data.updateClient(editingClient.id, patch);
            flash('Client updated');
          }}
          onUploadLogo={async (file) => {
            try {
              await data.uploadClientLogo(editingClient.id, file);
              flash('Logo uploaded');
            } catch (e) {
              flash(e instanceof Error ? `Upload failed: ${e.message}` : 'Upload failed');
              throw e;
            }
          }}
        />
      )}

      <Toast message={message} />
    </div>
  );
}
